<script setup lang="ts">
import AccordionComponent from "@/components/accordion/AccordionComponent.vue";
import { defineProps, inject, ref, defineModel, watch, computed } from "vue";
import { DestinyMatrix, DestinyMatrixKeysEnum } from "@/types/destinyMatrix";
import { useI18n } from "vue-i18n";
import AppSelect from "@/components/inputs/AppSelect.vue";
import ArrowButton from "@/views/Calculators/DestinyMatrix/components/buttons/ArrowButton.vue";

const props = defineProps<{
  matrix: DestinyMatrix | undefined;
}>();
const isOpen = defineModel<boolean>({ default: false });
const getDescription =
  inject<(key: DestinyMatrixKeysEnum) => Promise<never>>("getDescription");

const { t } = useI18n();

watch(
  () => props.matrix,
  () => (yearsValues.value = undefined)
);

const options = [
  "0",
  "1-2",
  "2-3",
  "3-4",
  "5",
  "6-7",
  "7-8",
  "8-9",
  "10",
  "11-12",
  "12-13",
  "13-14",
  "15",
  "16-17",
  "17-18",
  "18-19",
  "20",
  "21-22",
  "22-23",
  "23-24",
  "25",
  "26-27",
  "27-28",
  "28-29",
  "30",
  "31-32",
  "32-33",
  "33-34",
  "35",
  "36-37",
  "37-38",
  "38-39",
  "40",
  "41-42",
  "42-43",
  "43-44",
  "45",
  "46-47",
  "47-48",
  "48-49",
  "50",
  "51-52",
  "52-53",
  "53-54",
  "55",
  "56-57",
  "57-58",
  "58-59",
  "60",
  "61-62",
  "62-63",
  "63-64",
  "65",
  "66-67",
  "67-68",
  "68-69",
  "70",
  "71-72",
  "72-73",
  "73-74",
  "75",
  "76-77",
  "77-78",
  "78-79",
];

const yearsValues = ref();
const selectedOption = ref(0);
const yearValueIndex = computed(() => {
  if (props.matrix === undefined) {
    return 0;
  }
  let optionBatch = selectedOption.value % 8;
  if (optionBatch === 0) {
    const r1 = props.matrix.r1;
    const edgeValues = [r1.l, r1.tl, r1.t, r1.tr, r1.r, r1.br, r1.b, r1.bl];

    return edgeValues[selectedOption.value / 8];
  }
  optionBatch = Math.trunc(selectedOption.value / 8);
  const yearsBatch = props.matrix.years[optionBatch];

  return yearsBatch[selectedOption.value - optionBatch * 8 - 1];
});

async function getData() {
  if (!yearsValues.value && props.matrix && getDescription) {
    yearsValues.value = await getDescription(DestinyMatrixKeysEnum.YEAR_TASKS);
  }
}

function updateSelectedIndex(indexDelta: number) {
  const newIndex = selectedOption.value + indexDelta;
  if (newIndex >= 0 && newIndex < options.length) {
    selectedOption.value = newIndex;
  }
}
</script>

<template>
  <AccordionComponent type="white" @opened="getData" v-model="isOpen">
    <template #title>
      {{ t("calculators.destiny-matrix.accordions.year-tasks") }}
    </template>
    <template #faq-content>
      <div v-if="yearsValues" class="flex flex-col gap-4 sf-pro">
        <div
          class="flex items-center gap-2"
          :class="{
            'pl-[42px]': selectedOption === 0,
          }"
        >
          <ArrowButton
            v-if="selectedOption > 0"
            arrow-direction="left"
            @clicked="updateSelectedIndex(-1)"
          />
          <AppSelect
            class="w-[132px]"
            :items="options"
            :default-value="options[selectedOption]"
            @select-option="(newOption: string) => selectedOption = options.indexOf(newOption)"
          />
          <ArrowButton
            v-if="selectedOption !== options.length - 1"
            arrow-direction="right"
            @clicked="updateSelectedIndex(1)"
          />
        </div>
        <div class="whitespace-pre-line">
          {{ yearsValues[yearValueIndex] }}
        </div>
      </div>
    </template>
    <template #collapse-text-btn>
      {{ t("calculators.destiny-matrix.accordions.collapse") }}
    </template>
  </AccordionComponent>
</template>
